import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { BaseLayout, ContentLayout, Section } from '@/components/layout/BaseLayout';
import { ThemedCard, StatCard } from '@/components/ui/themed-card';
import { Button } from '@/components/ui/button';
import { 
  CreditCardIcon, 
  TargetIcon, 
  TicketIcon, 
  TrophyIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  CheckCircleIcon,
  TrendingUpIcon,
  StarIcon,
  BellIcon,
  GiftIcon,
  Users2Icon,
  CalendarIcon,
  DollarSignIcon
} from 'lucide-react';

export const DashboardPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('credits_balance')
        .eq('id', user?.id)
        .single();

      if (!error && data) {
        setCreditsBalance(data.credits_balance || 0);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/auth/login');
    }
  };

  const userMetadata = user?.user_metadata || {};
  const fullName = userMetadata.full_name || user?.email?.split('@')[0] || 'Usuário';
  
  // Check if user is admin
  const isAdmin = user?.email && ['admin@rifabr.com', 'fabiorizzo@gmail.com'].includes(user.email);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <BaseLayout variant="wide">
      <div className="space-y-8">
        {/* Welcome Header */}
        <motion.div 
          className="text-center py-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Bem-vindo, {fullName}! 🎯
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Aqui você pode participar de rifas, criar suas próprias rifas e gerenciar seus créditos.
          </p>
        </motion.div>
        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ThemedCard 
              className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-600/10 border-green-500/20 cursor-pointer hover:scale-105 transition-all duration-300"
              onClick={() => navigate('/credits')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium mb-1">Seus Créditos</p>
                  <p className="text-3xl font-bold text-foreground">
                    {loading ? '...' : formatCurrency(creditsBalance)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Clique para adicionar</p>
                </div>
                <div className="h-12 w-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <DollarSignIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </ThemedCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ThemedCard 
              className="p-6 bg-gradient-to-br from-blue-500/10 to-indigo-600/10 border-blue-500/20 cursor-pointer hover:scale-105 transition-all duration-300"
              onClick={() => navigate('/creator-dashboard')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium mb-1">Rifas Ativas</p>
                  <p className="text-3xl font-bold text-foreground">0</p>
                  <p className="text-xs text-muted-foreground mt-1">Gerencie suas rifas</p>
                </div>
                <div className="h-12 w-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <TargetIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </ThemedCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ThemedCard className="p-6 bg-gradient-to-br from-purple-500/10 to-violet-600/10 border-purple-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-medium mb-1">Seus Bilhetes</p>
                  <p className="text-3xl font-bold text-foreground">0</p>
                  <p className="text-xs text-muted-foreground mt-1">Participe de rifas</p>
                </div>
                <div className="h-12 w-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <TicketIcon className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </ThemedCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ThemedCard className="p-6 bg-gradient-to-br from-yellow-500/10 to-orange-600/10 border-yellow-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-600 text-sm font-medium mb-1">Prêmios Ganhos</p>
                  <p className="text-3xl font-bold text-foreground">0</p>
                  <p className="text-xs text-muted-foreground mt-1">Boa sorte!</p>
                </div>
                <div className="h-12 w-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <TrophyIcon className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </ThemedCard>
          </motion.div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="lg:col-span-2"
          >
            <ThemedCard className="p-6 h-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <BellIcon className="h-5 w-5 text-primary" />
                  Atividade Recente
                </h3>
                <Button variant="ghost" size="sm">Ver tudo</Button>
              </div>
              
              <div className="space-y-4">
                {/* Placeholder activity items */}
                <div className="flex items-center p-4 bg-muted/30 rounded-lg">
                  <div className="h-10 w-10 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                    <Users2Icon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Bem-vindo ao RifaBR!</p>
                    <p className="text-xs text-muted-foreground">Sua conta foi criada com sucesso</p>
                  </div>
                  <span className="text-xs text-muted-foreground">Agora</span>
                </div>
                
                <div className="flex items-center p-4 bg-muted/10 rounded-lg opacity-60">
                  <div className="h-10 w-10 bg-green-500/20 rounded-full flex items-center justify-center mr-3">
                    <GiftIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Crie sua primeira rifa</p>
                    <p className="text-xs text-muted-foreground">Comece a ganhar dinheiro com rifas online</p>
                  </div>
                  <Button size="sm" variant="outline">Criar</Button>
                </div>
                
                <div className="flex items-center p-4 bg-muted/10 rounded-lg opacity-60">
                  <div className="h-10 w-10 bg-purple-500/20 rounded-full flex items-center justify-center mr-3">
                    <StarIcon className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Participe de rifas populares</p>
                    <p className="text-xs text-muted-foreground">Veja as rifas em destaque</p>
                  </div>
                  <Button size="sm" variant="outline">Explorar</Button>
                </div>
              </div>
            </ThemedCard>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <ThemedCard className="p-6 h-full">
              <h3 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                <TrendingUpIcon className="h-5 w-5 text-primary" />
                Ações Rápidas
              </h3>
              
              <div className="space-y-3">
                <Button
                  className="w-full h-16 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 text-white border-0"
                  onClick={() => navigate('/create-raffle')}
                >
                  <div className="flex items-center gap-3">
                    <PlusIcon className="h-6 w-6" />
                    <div className="text-left">
                      <div className="font-semibold">Criar Rifa</div>
                      <div className="text-xs opacity-90">Comece agora</div>
                    </div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-14 border-green-200 hover:bg-green-50 dark:hover:bg-green-950"
                  onClick={() => navigate('/credits')}
                >
                  <div className="flex items-center gap-3">
                    <CreditCardIcon className="h-5 w-5 text-green-600" />
                    <div className="text-left">
                      <div className="font-medium text-foreground">Adicionar Créditos</div>
                      <div className="text-xs text-muted-foreground">Recarregue sua conta</div>
                    </div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-14 border-blue-200 hover:bg-blue-50 dark:hover:bg-blue-950"
                >
                  <div className="flex items-center gap-3">
                    <SearchIcon className="h-5 w-5 text-blue-600" />
                    <div className="text-left">
                      <div className="font-medium text-foreground">Explorar Rifas</div>
                      <div className="text-xs text-muted-foreground">Veja oportunidades</div>
                    </div>
                  </div>
                </Button>

                {isAdmin && (
                  <Button
                    variant="outline"
                    className="w-full h-14 border-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950"
                    onClick={() => navigate('/admin/payments')}
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-amber-600" />
                      <div className="text-left">
                        <div className="font-medium text-foreground">Validar Pagamentos</div>
                        <div className="text-xs text-muted-foreground">Painel admin</div>
                      </div>
                    </div>
                  </Button>
                )}
              </div>
            </ThemedCard>
          </motion.div>
        </div>

        {/* Popular Raffles Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-8"
        >
          <ThemedCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <StarIcon className="h-5 w-5 text-primary" />
                Rifas Populares
              </h3>
              <Button variant="ghost" size="sm">Ver todas</Button>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Example raffle cards */}
              <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">📱</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">iPhone 15 Pro</h4>
                    <p className="text-xs text-muted-foreground">128GB Titânio</p>
                  </div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">23/100 vendidos</span>
                  <span className="font-semibold text-primary">R$ 10,00</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                  <div className="bg-primary h-1.5 rounded-full w-[23%]" />
                </div>
              </div>

              <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">💰</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Vale R$ 5.000</h4>
                    <p className="text-xs text-muted-foreground">Dinheiro no PIX</p>
                  </div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">67/200 vendidos</span>
                  <span className="font-semibold text-primary">R$ 25,00</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                  <div className="bg-primary h-1.5 rounded-full w-[33%]" />
                </div>
              </div>

              <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">🚗</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Honda Civic 2024</h4>
                    <p className="text-xs text-muted-foreground">0km Completo</p>
                  </div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">89/500 vendidos</span>
                  <span className="font-semibold text-primary">R$ 100,00</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                  <div className="bg-primary h-1.5 rounded-full w-[18%]" />
                </div>
              </div>
            </div>
          </ThemedCard>
        </motion.div>
      </div>
    </BaseLayout>
  );
};